import { useState, useEffect, useCallback, useRef } from "react";

interface UseResizablePanelOptions {
  storageKey: string;
  defaultSize: number;
  minSize?: number;
  maxSize?: number;
  direction: "horizontal" | "vertical";
}

interface UseResizablePanelResult {
  size: number;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
}

export function useResizablePanel({
  storageKey,
  defaultSize,
  minSize = 100,
  maxSize = 800,
  direction,
}: UseResizablePanelOptions): UseResizablePanelResult {
  // Initialize size from localStorage or default
  const [size, setSize] = useState<number>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= minSize && parsed <= maxSize) {
          return parsed;
        }
      }
    }
    return defaultSize;
  });

  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef(0);
  const startSizeRef = useRef(0);
  const saveTimeoutRef = useRef<number | null>(null);

  // Debounced save to localStorage
  const saveToStorage = useCallback(
    (newSize: number) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = window.setTimeout(() => {
        localStorage.setItem(storageKey, String(newSize));
      }, 100);
    },
    [storageKey]
  );

  // Handle mouse down on resize handle
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      startPosRef.current = direction === "horizontal" ? e.clientX : e.clientY;
      startSizeRef.current = size;
    },
    [direction, size]
  );

  // Handle mouse move during drag
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const currentPos = direction === "horizontal" ? e.clientX : e.clientY;
      const delta = currentPos - startPosRef.current;

      // For vertical (bottom panel), dragging up (negative delta) should increase size
      // For horizontal (left panel), dragging right (positive delta) should increase size
      const newSize =
        direction === "vertical"
          ? startSizeRef.current - delta
          : startSizeRef.current + delta;

      const clampedSize = Math.max(minSize, Math.min(maxSize, newSize));
      setSize(clampedSize);
      saveToStorage(clampedSize);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, direction, minSize, maxSize, saveToStorage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    size,
    isDragging,
    handleMouseDown,
  };
}
